import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Bell, Check, CheckCheck, Package, CreditCard, Truck, PackageCheck, XCircle, Inbox } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { API, useAuth } from "@/App";

const POLL_MS = 45000; // 45s — light polling for unread badge

const TYPE_ICON = {
  order_placed: Package,
  order_paid: CreditCard,
  order_shipped: Truck,
  order_delivered: PackageCheck,
  order_cancelled: XCircle,
  system: Bell,
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnread(res.data?.count || 0);
    } catch (e) {
      // silent — likely logged out
      if (process.env.NODE_ENV !== "production") console.debug("[notif] unread fetch failed", e);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
      setItems(res.data?.notifications || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("[notif] failed to load notifications", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // initial unread fetch + light polling — only when logged in & tab visible
  useEffect(() => {
    if (!user) {
      setUnread(0);
      setItems([]);
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    fetchUnreadCount();
    pollRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchUnreadCount();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [user]);

  // when opened, pull the list
  useEffect(() => {
    if (open) fetchList();
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
      setItems((prev) => prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("[notif] mark-read failed", e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("[notif] mark-all-read failed", e);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 text-gray-600 hover:text-tbc-navy transition-colors"
          data-testid="notification-bell-trigger"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-tbc-gold text-tbc-navy text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0 border border-gray-200 shadow-xl"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-tbc-navy" />
            <span className="font-semibold text-tbc-navy text-sm">Notifications</span>
          </div>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-tbc-navy hover:text-tbc-gold inline-flex items-center gap-1"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="notification-list">
          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center" data-testid="notification-empty-state">
              <Inbox className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">You&apos;re all caught up</p>
              <p className="text-xs text-gray-400 mt-1">
                Order updates will appear here.
              </p>
            </div>
          ) : (
            items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              const inner = (
                <div className="flex items-start gap-3 px-4 py-3">
                  <div
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                      n.is_read ? "bg-gray-100 text-gray-500" : "bg-tbc-cream text-tbc-navy"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm leading-snug ${
                          n.is_read ? "text-gray-600" : "text-tbc-navy font-semibold"
                        }`}
                      >
                        {n.title}
                      </p>
                      {!n.is_read && (
                        <span className="mt-1 w-2 h-2 bg-tbc-gold rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[11px] text-gray-400">{timeAgo(n.created_at)}</span>
                      {!n.is_read && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            markRead(n.notification_id);
                          }}
                          className="text-[11px] text-gray-400 hover:text-tbc-navy inline-flex items-center gap-0.5"
                          data-testid={`notification-mark-read-${n.notification_id}`}
                        >
                          <Check className="w-3 h-3" />
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );

              const wrapperClasses = `block border-b border-gray-50 last:border-b-0 transition-colors ${
                n.is_read ? "bg-white hover:bg-gray-50" : "bg-tbc-cream/40 hover:bg-tbc-cream/70"
              }`;

              return n.link ? (
                <Link
                  key={n.notification_id}
                  to={n.link}
                  onClick={() => {
                    if (!n.is_read) markRead(n.notification_id);
                    setOpen(false);
                  }}
                  className={wrapperClasses}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={n.notification_id}
                  className={wrapperClasses}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  {inner}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
